import React, { Fragment, memo, useCallback, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import {
  AdminCancelInvoicePaymentRelease,
  AdminClearInvoicePaymentReleaseFailure,
  AdminScheduleInvoicePaymentRelease,
} from 'src/constants/actionTypes'
import { paymentReleaseActionLabels } from 'src/constants/invoice/constants'
import { PaymentReleaseActionKind } from 'src/constants/invoice/enums'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type {
  InvoicePaymentRelease,
  PaymentReleaseScheduleFormValues,
  PendingPaymentReleaseAction,
} from 'src/interfaces/invoice'
import { getPaymentReleaseFailure, getPaymentReleaseSubmitting } from 'src/selectors/invoice'
import {
  formatReleaseAt,
  formatScheduledAtInCentral,
  parseScheduledAt,
  toCentralWallClock,
} from 'src/utils/paymentReleaseTime'
import { paymentReleaseScheduleValidator } from './PaymentReleaseScheduleForm.validator'
import { PaymentReleaseScheduleFields } from './PaymentReleaseScheduleFields'
import { pickerValueFromWallClock, wallClockFromPicker } from './pickerWallClock'

const FORM_ID = 'payment-release-schedule-form'

interface Props {
  partnerId?: string
  pendingAction: PendingPaymentReleaseAction | null
  onClose(): void
}

/**
 * Schedule, reschedule and cancel an invoice's payment hold.
 *
 * Reschedule is the same POST as schedule — the server REPLACES an existing hold, so there is
 * never a cancel first. A success patches the row in the store from the response; a stale-row
 * failure (the hold already fired, was cancelled…) refetches the list from the saga. Failures
 * are shown inline from the store, classified by the error NAME, so the modal does not toast.
 */
const PaymentReleaseModalComponent = ({ partnerId, pendingAction, onClose }: Props) => {
  const dispatch = useDispatch()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const isSubmitting = useSelector(getPaymentReleaseSubmitting)
  const failure = useSelector(getPaymentReleaseFailure)

  // A new modal never opens over the previous one's failure.
  useEffect(() => {
    dispatch(AdminClearInvoicePaymentReleaseFailure())
  }, [dispatch, pendingAction])

  const isCancel = pendingAction?.kind === PaymentReleaseActionKind.Cancel
  const partnerInvoice = pendingAction?.partnerInvoice ?? null
  const currentScheduledAt = formatScheduledAtInCentral(partnerInvoice?.paymentReleaseScheduledAt)

  // A reschedule starts from the hold's own Central wall clock; a new one starts empty, so the
  // admin always makes a deliberate pick.
  const initialValues = useMemo<PaymentReleaseScheduleFormValues>(() => {
    const scheduledAt = parseScheduledAt(partnerInvoice?.paymentReleaseScheduledAt)

    if (pendingAction?.kind !== PaymentReleaseActionKind.Reschedule || !scheduledAt) {
      return { releaseAt: null }
    }

    return { releaseAt: pickerValueFromWallClock(toCentralWallClock(scheduledAt)) }
  }, [pendingAction, partnerInvoice])

  const handleClose = useCallback(() => {
    // The request is a write against a payment hold; closing mid-flight would hide its outcome.
    if (isSubmitting) {
      return
    }

    dispatch(AdminClearInvoicePaymentReleaseFailure())
    onClose()
  }, [dispatch, isSubmitting, onClose])

  const handleSucceeded = useCallback(
    (message: string) => {
      snackbar?.enqueueSnackbar(message, { variant: 'success' })
      onClose()
    },
    [onClose, snackbar],
  )

  const handleSchedule = useCallback(
    async (values: PaymentReleaseScheduleFormValues) => {
      if (!pendingAction || !partnerId || !values.releaseAt) {
        return
      }

      // The Central wall clock exactly as picked — never the local time, never UTC.
      const releaseAt = formatReleaseAt(wallClockFromPicker(values.releaseAt))

      // Silent: the failure is rendered inline from the store, by error name.
      const release = await asyncDispatchWithErrorToast<InvoicePaymentRelease>(
        AdminScheduleInvoicePaymentRelease.request({
          partnerId,
          invoiceId: pendingAction.partnerInvoice.id,
          releaseAt,
        }),
        true,
      )

      if (!release) {
        return
      }

      // Read back from the stored UTC instant, converted to Central — not the input echoed.
      const scheduledFor = formatScheduledAtInCentral(release.paymentReleaseScheduledAt)

      handleSucceeded(
        scheduledFor
          ? `Payment release scheduled for ${scheduledFor}`
          : 'Payment release scheduled',
      )
    },
    [asyncDispatchWithErrorToast, handleSucceeded, partnerId, pendingAction],
  )

  const handleCancelRelease = useCallback(async () => {
    if (!pendingAction || !partnerId) {
      return
    }

    const release = await asyncDispatchWithErrorToast<InvoicePaymentRelease>(
      AdminCancelInvoicePaymentRelease.request({
        partnerId,
        invoiceId: pendingAction.partnerInvoice.id,
      }),
      true,
    )

    if (!release) {
      return
    }

    handleSucceeded('Scheduled payment release cancelled')
  }, [asyncDispatchWithErrorToast, handleSucceeded, partnerId, pendingAction])

  if (!pendingAction) {
    return null
  }

  const submitLabel =
    pendingAction.kind === PaymentReleaseActionKind.Reschedule ? 'Reschedule' : 'Schedule'
  const cancelSummary = currentScheduledAt
    ? `The hold set for ${currentScheduledAt} is removed. `
    : 'The hold is removed. '

  let submitButton = (
    <Button
      size="small"
      color="dark"
      type="submit"
      form={FORM_ID}
      loading={isSubmitting}
      disabled={isSubmitting}
      data-testid="payment-release-schedule-button"
    >
      {submitLabel}
    </Button>
  )

  if (isCancel) {
    submitButton = (
      <Button
        size="small"
        color="red"
        loading={isSubmitting}
        disabled={isSubmitting}
        onClick={handleCancelRelease}
        data-testid="payment-release-cancel-button"
      >
        Cancel release
      </Button>
    )
  }

  let buttons = (
    <Fragment>
      <Button size="small" color="grey" disabled={isSubmitting} onClick={handleClose}>
        {isCancel ? 'Keep release' : 'Close'}
      </Button>
      {submitButton}
    </Fragment>
  )

  // Sending the same request again cannot succeed (the hold already fired, the partner
  // auto-releases…), so only Close is offered.
  if (failure && !failure.isRetryable) {
    buttons = (
      <Button
        size="small"
        color="dark"
        onClick={handleClose}
        data-testid="payment-release-close-button"
      >
        Close
      </Button>
    )
  }

  return (
    <CommonModal
      isOpen
      title={`${paymentReleaseActionLabels[pendingAction.kind]}?`}
      className={tw`max-w-[480px]`}
      buttons={buttons}
      onClose={handleClose}
    >
      <div css={tw`flex flex-col gap-4`} data-testid="payment-release-modal">
        <Typography variant="body2">
          Invoice <strong>{pendingAction.invoiceLabel}</strong> in{' '}
          {pendingAction.composedInvoiceLabel}.
        </Typography>

        {currentScheduledAt && !isCancel ? (
          <Typography variant="body2" css={tw`text-shades-500`}>
            Currently scheduled for {currentScheduledAt}. Saving replaces it.
          </Typography>
        ) : null}

        {isCancel ? (
          <Typography variant="body2">
            {cancelSummary}
            Driver payments stay where they are and can be released by hand or scheduled again.
          </Typography>
        ) : (
          <Formik<PaymentReleaseScheduleFormValues>
            initialValues={initialValues}
            enableReinitialize
            validationSchema={paymentReleaseScheduleValidator}
            onSubmit={handleSchedule}
          >
            <Form id={FORM_ID} data-testid="payment-release-schedule-form">
              <PaymentReleaseScheduleFields />
            </Form>
          </Formik>
        )}

        {failure ? (
          <div
            css={tw`rounded-lg border border-solid border-critical-500 bg-critical-50 p-3`}
            role="alert"
            data-testid={`payment-release-error-${failure.kind}`}
          >
            <Typography variant="body2" css={tw`text-critical-500`}>
              {failure.message}
            </Typography>
          </div>
        ) : null}
      </div>
    </CommonModal>
  )
}

export const PaymentReleaseModal = memo(PaymentReleaseModalComponent)
